import { BaseService } from "../baseService";
import { RelabelCaseAPIService } from "./relabelCaseAPIService";
import { sanitiseParams } from "src/utils";
import {
  PaginatedRelabelCases,
  RelabelCasePaginationOptions,
  RelabelCaseRecord,
  RelabelCaseStatus,
  S3SignedUrl
} from "src/types";
import { PostRelabelCasesRequestBody } from "src/types/api/relabelCases/postRelabelCaseRecord";
import { GetRelabelCaseStatsResponse } from "src/types/api/relabelCases/getRelabelCaseStats";
import { PatchRelabelCaseQuantityChange } from "src/types/api/relabelCases/patchRelabelCaseQuantity";
import { PatchRelabelCaseSimpleEditRequestBody } from "src/types/api/relabelCases/patchRelabelCaseSimpleEdit";
import { PostRelabelCaseRouteResponse } from "src/types/api/relabelCases/postRelabelCaseRoute";
import { PostRelabelCaseConfirmPlacementResponse } from "src/types/api/relabelCases/postRelabelCaseConfirmPlacement";

class RelabelCaseService extends BaseService<RelabelCaseAPIService> {
  @sanitiseParams()
  async createRelabelCases(requestBody: PostRelabelCasesRequestBody): Promise<RelabelCaseRecord[]> {
    return await this.apiService.postRelabelCases(requestBody);
  }

  @sanitiseParams()
  async getRelabelCasesByPurchaseOrderId(purchaseOrderId: string): Promise<RelabelCaseRecord[]> {
    return await this.apiService.getRelabelCasesByPurchaseOrderId(purchaseOrderId);
  }

  @sanitiseParams()
  async getRelabelCasesByTransferOrderId(transferOrderId: string): Promise<RelabelCaseRecord[]> {
    return await this.apiService.getRelabelCasesByTransferOrderId(transferOrderId);
  }

  async getRelabelCases(queryParams?: RelabelCasePaginationOptions): Promise<PaginatedRelabelCases> {
    return await this.apiService.getRelabelCases(queryParams);
  }

  @sanitiseParams()
  async updateCaseStatus(caseId: string, status: RelabelCaseStatus): Promise<RelabelCaseRecord> {
    return await this.apiService.patchRelabelCaseStatus(caseId, status);
  }

  @sanitiseParams()
  async updateCaseProductQuantities(
    caseId: string,
    changes: PatchRelabelCaseQuantityChange[]
  ): Promise<RelabelCaseRecord> {
    return await this.apiService.patchRelabelCaseQuantity(caseId, { changes });
  }

  @sanitiseParams()
  async simpleEditCaseProduct(caseId: string, body: PatchRelabelCaseSimpleEditRequestBody): Promise<RelabelCaseRecord> {
    return await this.apiService.patchRelabelCaseSimpleEdit(caseId, body);
  }

  @sanitiseParams()
  async getRelabelCaseLabelSignedUrl(caseId: string): Promise<S3SignedUrl> {
    return await this.apiService.getRelabelCaseLabelSignedUrl(caseId);
  }

  async getRelabelCaseStats(): Promise<GetRelabelCaseStatsResponse> {
    return await this.apiService.getRelabelCaseStats();
  }

  async getCompleteCases(queryParams?: RelabelCasePaginationOptions): Promise<PaginatedRelabelCases> {
    return await this.apiService.getCompleteCases(queryParams);
  }

  @sanitiseParams()
  async routeCase(caseId: string): Promise<PostRelabelCaseRouteResponse> {
    return await this.apiService.postRelabelCaseRoute(caseId);
  }

  @sanitiseParams()
  async cancelRouting(caseId: string): Promise<RelabelCaseRecord> {
    return await this.apiService.deleteRelabelCaseRoute(caseId);
  }

  @sanitiseParams()
  async confirmCasePlacement(caseId: string, locationId: string): Promise<PostRelabelCaseConfirmPlacementResponse> {
    return await this.apiService.postRelabelCaseConfirmPlacement(caseId, { locationId });
  }

  @sanitiseParams()
  async recordCasePrintEvent(caseId: string, body: { printerName: string; printerUid?: string }): Promise<void> {
    return await this.apiService.postRelabelCasePrintEvent(caseId, body);
  }
}

export { RelabelCaseService };
